// Sugalarm — a tab unit hint that is shown whole or not at all (cycle 451.3).
//
// The Log type tabs clip their content (`.tab { overflow: hidden }`, cycle
// 159). On the narrow 1024-wide kiosk column the Exercise tab has no room for
// its "min" hint, so it rendered as a hidden fragment past the button edge.
// This renders the hint as the tab button's last child, measures the button's
// label plus the hint against the button's content box before paint, and
// sets `hidden` when they don't both fit. It never changes the tab's size.
//
// Measurements come from getBoundingClientRect, which is SCREEN px on the
// transform-scaled kiosk stage; every term is read the same way (padding is
// scaled by the button's rect/offset ratio), so the comparison is exact.

import { useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

interface Props {
  children: string;
  style?: CSSProperties;
}

/** Does `label + hint` fit inside the button's content box? `hintWidth` is the
 *  hint's natural width in screen px (measured while it was shown). */
export function hintFits(
  button: HTMLElement,
  hint: HTMLElement,
  hintWidth: number,
): boolean {
  const rect = button.getBoundingClientRect();
  const scale = button.offsetWidth > 0 ? rect.width / button.offsetWidth : 1;
  const cs = getComputedStyle(button);
  const padL = parseFloat(cs.paddingLeft) * scale;
  const padR = parseFloat(cs.paddingRight) * scale;
  const border =
    (parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth)) * scale;
  const gap = parseFloat(getComputedStyle(hint).marginLeft) * scale;
  let label = 0;
  for (const node of Array.from(button.childNodes)) {
    if (node.nodeType !== Node.TEXT_NODE) continue;
    const range = document.createRange();
    range.selectNodeContents(node);
    label += range.getBoundingClientRect().width;
  }
  return padL + label + gap + hintWidth + padR + border <= rect.width + 0.5;
}

export function FitOrHideHint({ children, style }: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const naturalWidth = useRef(0);
  const [fits, setFits] = useState(true);
  // Bumped when a late webfont lands: the hint is re-shown for one layout
  // pass so its natural width is re-read at the loaded font (before paint).
  const [epoch, setEpoch] = useState(0);

  useLayoutEffect(() => {
    const hint = ref.current;
    const button = hint?.parentElement;
    if (!hint || !button) return;
    const measure = () => {
      // The hint's own width can only be read while it is shown; keep the
      // last reading for when it is hidden.
      if (!hint.hidden) {
        const w = hint.getBoundingClientRect().width;
        if (w > 0) naturalWidth.current = w;
      }
      if (naturalWidth.current === 0) return;
      setFits(hintFits(button, hint, naturalWidth.current));
    };
    measure();
    // An old WebView without ResizeObserver keeps the first measurement.
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(button);
    return () => ro.disconnect();
  }, [epoch]);

  useLayoutEffect(() => {
    let live = true;
    void document.fonts?.ready.then(() => {
      if (!live) return;
      setFits(true);
      setEpoch((e) => e + 1);
    });
    return () => {
      live = false;
    };
  }, []);

  return (
    <span ref={ref} hidden={!fits} style={style}>
      {children}
    </span>
  );
}
